import {Injectable, NgZone, signal} from '@angular/core';
import {Howl} from 'howler';
import {AudioTrack} from './audio.service';
import {AudioStateService} from './audio-state.service';

/**
 * Simple audio player specifically for gaming mode
 * No start/end time logic - just plays full songs from beginning to end
 */
@Injectable({
  providedIn: 'root'
})
export class GamingAudioPlayerService {

  private howlSound = signal<Howl | null>(null);
  private timeUpdateInterval = signal<any>(null);

  constructor(
    private ngZone: NgZone,
    private stateService: AudioStateService
  ) {}

  /**
   * Play a track in gaming mode - simple full song playback
   */
  playTrack(index: number): void {
    console.log(`Gaming mode: Playing track at index ${index}`);
    const tracks = this.stateService.tracks();

    if (index < 0 || index >= tracks.length) {
      console.warn(`Invalid track index: ${index}`);
      return;
    }

    // Clean up any existing audio
    this.cleanup();

    // Update state
    this.stateService.setCurrentTrackIndex(index);
    this.stateService.setCurrentTime(0);
    this.stateService.setIsLoading(true);

    const track = tracks[index];
    this.createHowl(track);
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause(): void {
    const sound = this.howlSound();
    if (!sound) {
      console.warn('No audio loaded for play/pause');
      return;
    }

    if (this.stateService.isPlaying()) {
      this.stateService.setIsPlaying(false);
      this.clearTimeUpdate();
      sound.pause();
    } else {
      this.stateService.setIsPlaying(true);
      this.startTimeUpdate();
      sound.play();
    }
  }

  /**
   * Stop playback
   */
  stop(): void {
    const sound = this.howlSound();
    if (sound) {
      sound.stop();
    }
    this.stateService.setIsPlaying(false);
    this.stateService.setCurrentTime(0);
    this.clearTimeUpdate();
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.clearTimeUpdate();

    const sound = this.howlSound();
    if (sound) {
      sound.stop();
      sound.off();
      sound.unload();
    }

    this.howlSound.set(null);
  }

  /**
   * Get current Howl instance
   */
  getHowlSound(): Howl | null {
    return this.howlSound();
  }

  /**
   * Create simple Howl instance for gaming mode
   */
  private createHowl(track: AudioTrack): void {
    console.log(`Gaming mode: Creating Howl for ${track.title}`);

    const howl = new Howl({
      src: [track.url],
      volume: this.stateService.volume(),
      onload: () => {
        console.log('Gaming mode: Audio loaded');
        const duration = howl.duration();

        // Set both duration and actual duration for gaming mode
        this.stateService.setActualAudioDuration(duration);
        this.stateService.setIsLoading(false);

        // Start playing immediately
        howl.play();
      },
      onplay: () => {
        console.log('Gaming mode: Audio started playing');
        this.stateService.setIsPlaying(true);
        this.startTimeUpdate();
      },
      onpause: () => {
        console.log('Gaming mode: Audio paused');
        this.stateService.setIsPlaying(false);
        this.clearTimeUpdate();
      },
      onstop: () => {
        console.log('Gaming mode: Audio stopped');
        this.stateService.setIsPlaying(false);
        this.clearTimeUpdate();
      },
      onend: () => {
        console.log('Gaming mode: Audio ended naturally');
        this.stateService.setIsPlaying(false);
        this.clearTimeUpdate();
        // In gaming mode, loop to next track or restart slideshow
      },
      onloaderror: (id, error) => {
        console.error('Gaming mode: Load error:', error);
        this.stateService.setIsLoading(false);
      },
      onplayerror: (id, error) => {
        console.error('Gaming mode: Play error:', error);
        this.stateService.setIsLoading(false);
      }
    });

    this.howlSound.set(howl);
  }

  /**
   * Simple time tracking - no end time logic
   */
  private startTimeUpdate(): void {
    this.clearTimeUpdate();

    const interval = setInterval(() => {
      const sound = this.howlSound();
      if (sound && this.stateService.isPlaying()) {
        const currentTime = sound.seek() as number;
        this.stateService.setCurrentTime(currentTime);
        // No end time checking - let song play naturally
      }
    }, 250);

    this.timeUpdateInterval.set(interval);
  }

  /**
   * Clear time update interval
   */
  private clearTimeUpdate(): void {
    const interval = this.timeUpdateInterval();
    if (interval) {
      clearInterval(interval);
      this.timeUpdateInterval.set(null);
    }
  }
}